const _fgEN = typeof window!=='undefined' && window.location.hostname==='cad.retaliq.net'
// ─── ForensicGallery ──────────────────────────────────────────────────────────
//
// Reemplaza el viejo ForensicMonitor con una galería buscable inspirada
// en VSaaS Forensic Analytics:
//   - Filtros (sitio, cámara, rango, clases) en panel derecho
//   - Galería de thumbnails (crops) en el centro
//   - Click en thumbnail → modal con frame completo + botón "Ver video NVR"
//
// El crop se baja DIRECTO del M4 vía Tailscale, sin pasar por la nube:
//   GET https://latadia.copec.retaliq.net/sensors/<uuid>/crop.jpg?token=...
//
// El token (`grant`) se pide una vez al backend al cambiar de sitio y dura
// 1 hora. Cuando expira, se renueva automáticamente al detectar 401.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuthStore } from '../store/authStore'
import { getSiteM4Host } from './HlsPlayer'

// Mapeo class → severidad para mostrar badge + sugerencias de UI.
// Mantener en sync con edge/sensors/severity.py
const CLASS_SEVERITY = {
  arma_de_fuego: 'critical', fuego: 'critical', humo: 'critical',
  line_crossing: 'high',
  loitering:     'medium',
  patente: 'medium', license_plate: 'medium',
  person: 'low', car: 'low', truck: 'low', bus: 'low',
  motorcycle: 'low', bicycle: 'low',
  backpack: 'low', handbag: 'low',
}

// Catálogo de clases disponibles para filtro. Subset por ahora de lo que
// realmente detectamos (sin armas/fuego/humo todavía).
const AVAILABLE_CLASSES = [
  { value: 'person',        label: 'Persona' },
  { value: 'car',           label: 'Auto' },
  { value: 'truck',         label: 'Camión' },
  { value: 'bus',           label: 'Bus' },
  { value: 'motorcycle',    label: 'Moto' },
  { value: 'bicycle',       label: 'Bicicleta' },
  { value: 'license_plate', label: 'Patente' },
  { value: 'loitering',     label: 'Merodeo' },
  { value: 'line_crossing', label: 'Cruce línea' },
]

// Ventanas predefinidas para el rango (minutos desde ahora).
const QUICK_RANGES = [
  { id: '15m',  label: '15 min',  mins: 15 },
  { id: '1h',   label: '1 hora',  mins: 60 },
  { id: '4h',   label: '4 horas', mins: 60 * 4 },
  { id: '24h',  label: '24 horas',mins: 60 * 24 },
  { id: '7d',   label: '7 días',  mins: 60 * 24 * 7 },
]

const SEVERITIES = [
  { value: 'critical', label: 'Crítico' },
  { value: 'high',     label: 'Alto' },
  { value: 'medium',   label: 'Medio' },
  { value: 'low',      label: 'Bajo' },
]

const SEV_COLOR = {
  critical: '#ef4444',
  high:     '#f97316',
  medium:   '#f59e0b',
  low:      '#6b7280',
}

// ISO YYYY-MM-DD para hoy en local time (date inputs HTML usan local).
function todayLocalISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

function severityOf(label) {
  return CLASS_SEVERITY[label] || 'low'
}


export default function ForensicGallery() {
  const token = useAuthStore(s => s.token)

  // ─── Filtros ────────────────────────────────────────────────────────
  const [sites, setSites]         = useState([])
  const [siteId, setSiteId]       = useState(null)
  const [cameras, setCameras]     = useState([])
  const [pickedCams, setPickedCams] = useState([])     // multi-select
  // Default vacío = todas las clases. Antes era ['person','car'] lo que
  // forzaba a Paulo a marcar manualmente otras clases para verlas.
  const [pickedClasses, setPickedClasses] = useState([])
  // Modo "ventana relativa" (default) vs "rango absoluto" (date + horas).
  // Relative es lo más natural para "qué pasó en la última hora",
  // absolute es para investigación específica ("entre las 14:00 y 16:00 del jueves").
  const [rangeMode, setRangeMode] = useState('relative')  // 'relative' | 'absolute'
  const [windowMins, setWindowMins] = useState(60)
  const [absDate, setAbsDate]   = useState(todayLocalISO())
  const [absFromH, setAbsFromH] = useState(0)
  const [absToH, setAbsToH]     = useState(23)
  // Filtros extra
  const [pickedSeverities, setPickedSeverities] = useState([])  // [] = todas
  const [plateQuery, setPlateQuery] = useState('')              // texto LPR
  const [minConfidence, setMinConfidence] = useState(0.6)        // 0..1

  // ─── Resultados y UI ───────────────────────────────────────────────
  //
  // Separación de responsabilidades:
  //   rawItems     — lo que viene del backend con la última window/rango.
  //                  SOLO se reescribe en runSearch().
  //   items        — filtrado client-side en vivo (cámaras, clases, severidad,
  //                  patente, confianza). Tocar cualquier pill actualiza la
  //                  galería al instante sin refetch al backend.
  const [rawItems, setRawItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)
  const [openItem, setOpenItem] = useState(null)        // modal detalle

  // ─── Token para sensores ───────────────────────────────────────────
  // {token, base_url, expires_in, expires_at_ms}
  const [sensorsGrant, setSensorsGrant] = useState(null)

  const fetchSensorsGrant = useCallback(async (sid) => {
    if (!sid) return
    try {
      const r = await fetch(`/api/sensors/${sid}/grant`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      if (!r.ok) throw new Error(`grant ${r.status}`)
      const g = await r.json()
      setSensorsGrant({
        ...g,
        expires_at_ms: Date.now() + (g.expires_in - 30) * 1000,  // 30s margen
      })
    } catch (e) {
      console.warn('[ForensicGallery] sensors grant falló', e.message)
    }
  }, [token])

  // ─── Cargar sitios al montar ────────────────────────────────────────
  // Importante esperar a que `token` esté hidratado: en el primer render
  // tras un reload, authStore arranca con token=null y refresh() lo llena
  // asíncrono. Sin este guard el fetch va sin Authorization y devuelve 401.
  useEffect(() => {
    console.log('[ForensicGallery] sites effect token=', token ? 'present' : 'null')
    if (!token) return
    // Bug #113: el dropdown queda vacío cuando:
    //   - el JWT está expirado (status 401 → return [])
    //   - /sites/status falla por algún reason transitorio
    //   - el array viene vacío real (tenant sin sitios)
    // Fallback A: si /sites/status falla, probar /sites/ (endpoint más permisivo).
    // Fallback B: si el JWT está expirado, intentar refresh vía authStore.
    const loadSites = async () => {
      const tryEndpoint = async (path) => {
        const r = await fetch(path, { headers: { Authorization: `Bearer ${token}` } })
        console.log(`[ForensicGallery] ${path} status`, r.status)
        if (r.status === 401) {
          // Intentar refresh vía authStore.refresh() si existe
          try {
            const refresh = useAuthStore.getState().refresh
            if (refresh) {
              await refresh()
              const t2 = useAuthStore.getState().token
              if (t2 && t2 !== token) {
                const r2 = await fetch(path, { headers: { Authorization: `Bearer ${t2}` } })
                if (r2.ok) return r2.json()
              }
            }
          } catch (e) { console.warn('[ForensicGallery] refresh failed', e) }
          throw new Error('401')
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      }
      let d
      try { d = await tryEndpoint('/api/sites/status') }
      catch (e1) {
        console.warn('[ForensicGallery] /api/sites/status failed, fallback to /api/sites/', e1.message)
        try { d = await tryEndpoint('/api/sites/') }
        catch (e2) {
          console.error('[ForensicGallery] both endpoints failed', e2.message)
          return
        }
      }
      const list = Array.isArray(d) ? d : (d.items || d.sites || [])
      console.log('[ForensicGallery] sites parsed n=', list.length)
      setSites(list)
      if (list.length && !siteId) {
        const first = list.find(s => getSiteM4Host(s.id)) || list[0]
        setSiteId(first.id)
      }
    }
    loadSites()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // ─── Cargar cámaras del sitio elegido ──────────────────────────────
  // /api/analytics/cameras devuelve un ARRAY directo (no envuelto en items).
  // Ese detalle me costó un rato en producción — comprobé al ver "0/0" en la UI.
  useEffect(() => {
    if (!siteId || !token) return
    fetch(`/api/analytics/cameras?site_id=${siteId}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        const arr = Array.isArray(d) ? d : (d.items || [])
        const list = arr.map(c => ({ channel: c.channel, name: c.name }))
        setCameras(list)
        // NO pre-marcar todas las cámaras. Los eventos en DB pueden tener
        // nombres viejos (firmware o pre-rename) mientras el CAD ya muestra
        // los nuevos del remote_config — y un filtro `includes` con la lista
        // nueva descartaría TODOS los eventos viejos. Default vacío = sin
        // filtrar por cámara; el operador marca selectivamente lo que quiere.
        setPickedCams([])
      })
      .catch(() => {})
    fetchSensorsGrant(siteId)
  }, [siteId, token, fetchSensorsGrant])

  // Auto-search: cuando todo está listo (sitio elegido + grant del M4 + token),
  // ejecutar búsqueda automática. Sin esto el operador veía la galería vacía
  // y tenía que clickear Buscar manual.
  const [autoSearched, setAutoSearched] = useState(false)
  useEffect(() => {
    if (!token || !siteId || !sensorsGrant) return
    if (autoSearched) return
    setAutoSearched(true)
    // Llamada diferida para no chocar con el render
    setTimeout(() => runSearch(), 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, siteId, sensorsGrant])

  // ─── Ejecutar búsqueda ─────────────────────────────────────────────
  //
  // El backend solo conoce eventos con su payload (incluyendo sensor_uuid).
  // Los crops/frames viven en el M4. La consulta:
  //   1) Pide al backend los detections del rango (server-side filter: ts).
  //   2) Aplica filtros cliente-side: cámara, clase, severidad, patente,
  //      confianza. Esto evita inflar la API con cada permutación de filtro
  //      mientras el volumen sea ≤2000 detections/búsqueda.
  //   3) Filtra a items con sensor_uuid (los pre-refactor no lo tienen).
  const runSearch = useCallback(async () => {
    if (!siteId || !token) return
    console.log('[ForensicGallery] runSearch — siteId:', siteId, 'windowMins:', windowMins, 'mode:', rangeMode)
    setLoading(true); setError(null)
    try {
      // Determinar ventana en minutos (cubre ambos modos: el server siempre
      // recibe window_minutes desde "ahora hacia atrás" y nosotros filtramos
      // por ts exacto cliente-side para el modo absolute).
      let serverWindowMins = windowMins
      let absStartMs = null, absEndMs = null
      if (rangeMode === 'absolute') {
        const absStart = new Date(`${absDate}T${String(absFromH).padStart(2,'0')}:00:00`)
        const absEnd   = new Date(`${absDate}T${String(absToH).padStart(2,'0')}:59:59`)
        absStartMs = absStart.getTime()
        absEndMs   = absEnd.getTime()
        // Calcular cuántos minutos hacia atrás cubre el rango pedido
        const now = Date.now()
        const minsBack = Math.ceil((now - absStartMs) / 60000) + 1
        serverWindowMins = Math.max(60, Math.min(minsBack, 60 * 24 * 30))
      }
      const params = new URLSearchParams({
        site_id: String(siteId),
        window_minutes: String(serverWindowMins),
        limit: '500',
        event_type: 'all',
      })
      const r = await fetch(`/api/analytics/detections?${params}`,
        { headers: { Authorization: `Bearer ${token}` } })
      if (!r.ok) throw new Error(`search ${r.status}`)
      const d = await r.json()
      // SOLO filtros server-side acá: rango ts + presencia de sensor_uuid.
      // Los filtros client-side (cámaras, clases, severidad, patente,
      // confianza) los aplica el useMemo de abajo en vivo.
      const rows = (d.items || []).filter(it => {
        if (!it.payload?.sensor_uuid) return false
        if (absStartMs !== null) {
          const tms = new Date(it.ts).getTime()
          if (tms < absStartMs || tms > absEndMs) return false
        }
        return true
      })
      setRawItems(rows)
    } catch (e) {
      setError(e.message)
      setRawItems([])
    } finally {
      setLoading(false)
    }
  }, [
    siteId, token, rangeMode, windowMins, absDate, absFromH, absToH,
  ])

  // ─── Filtrado en vivo (client-side) ────────────────────────────────
  //
  // Cambiar cualquier pill (cámaras/clases/severidad/patente/confianza)
  // recomputa la galería al instante. No refetch al backend.
  const items = useMemo(() => {
    const plateQ = plateQuery.trim().toUpperCase()
    const result = rawItems.filter(it => {
      // Cámaras (si hay selección activa)
      if (pickedCams.length > 0 && !pickedCams.includes(it.camera_name))
        return false
      // Clases
      const lbl = labelOfEvent(it)
      if (pickedClasses.length > 0 && !pickedClasses.includes(lbl))
        return false
      // Severidad (derivada de la clase)
      const sev = severityOf(lbl)
      if (pickedSeverities.length > 0 && !pickedSeverities.includes(sev))
        return false
      // Patente (substring case-insensitive)
      if (plateQ && !(it.plate || '').toUpperCase().includes(plateQ))
        return false
      // Confianza mínima
      if ((it.confidence || 0) < minConfidence)
        return false
      return true
    })
    // Log para debug: aparece cada vez que cambia un filtro
    console.log('[ForensicGallery] filter →',
      result.length, '/', rawItems.length,
      'cams:', pickedCams.length, 'cls:', pickedClasses.length,
      'sev:', pickedSeverities.length, 'minConf:', minConfidence,
    )
    return result
  }, [
    rawItems, pickedCams, pickedClasses, pickedSeverities, plateQuery, minConfidence,
  ])

  // ─── URL para el crop/frame de un sensor ───────────────────────────
  const buildSensorUrl = useCallback((sensor_uuid, kind = 'crop') => {
    if (!sensor_uuid || !sensorsGrant) return null
    return `${sensorsGrant.base_url}/sensors/${sensor_uuid}/${kind}.jpg`
         + `?token=${encodeURIComponent(sensorsGrant.token)}`
  }, [sensorsGrant])

  // ─── URL para el clip MP4 6s del Incident Clip ─────────────────────
  // /sensors/<uuid>/clip.mp4 — sidecar M4 sirve el clip que recortó del
  // ring buffer HLS local. Si no existe (todavía generándose o purgado
  // del edge), el endpoint redirige al cloud o 404.
  // El <video> HTML5 lo carga directo con +faststart.
  const buildClipUrl = useCallback((sensor_uuid) => {
    if (!sensor_uuid || !sensorsGrant) return null
    return `${sensorsGrant.base_url}/sensors/${sensor_uuid}/clip.mp4`
         + `?token=${encodeURIComponent(sensorsGrant.token)}`
  }, [sensorsGrant])

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* ─── Galería (centro) ─────────────────────────────────────── */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        background: 'var(--bg-root)',
      }}>
        {/* Header con resumen y botón refresh */}
        <div style={{
          padding: '10px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 12,
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>
            ◆ Análisis forense
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {loading ? (_fgEN ? 'Loading…' : 'Cargando…') :
              items.length > 0 ? `${items.length} hallazgos` :
              (_fgEN ? 'No results — adjust filters and press Search' : 'Sin resultados — ajusta filtros y presiona Buscar')}
          </span>
          <div style={{ flex: 1 }} />
          {error && (
            <span style={{ fontSize: 11, color: '#ef4444' }}>
              Error: {error}
            </span>
          )}
        </div>

        {/* Grid de thumbnails */}
        <div style={{
          flex: 1, overflow: 'auto', padding: 12,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 8,
          alignContent: 'start',
        }}>
          {items.map(it => (
            <SensorThumb
              key={it.id}
              item={it}
              cropUrl={buildSensorUrl(it.payload.sensor_uuid, 'crop')}
              onClick={() => setOpenItem(it)}
            />
          ))}
          {!loading && items.length === 0 && (
            <div style={{
              gridColumn: '1 / -1',
              padding: 40, textAlign: 'center',
              color: 'var(--text-muted)', fontSize: 12,
            }}>
              No hay sensores que coincidan con los filtros.
              <br /><br />
              Tip: aumenta el rango de tiempo o agrega más clases.
            </div>
          )}
        </div>
      </div>

      {/* ─── Panel de filtros (derecha) ────────────────────────────── */}
      <div style={{
        width: 280, flexShrink: 0,
        borderLeft: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        display: 'flex', flexDirection: 'column',
        overflow: 'auto',
      }}>
        <div style={{ padding: 14, borderBottom: '1px solid var(--border)' }}>
          <FilterLabel>{_fgEN ? 'Site' : 'Sitio'}</FilterLabel>
          <select
            value={siteId || ''}
            onChange={e => setSiteId(Number(e.target.value))}
            style={selectStyle}
          >
            {sites.length === 0 && (
              <option value="">(Sin sitios disponibles — recarga la página)</option>
            )}
            {sites.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        <div style={{ padding: 14, borderBottom: '1px solid var(--border)' }}>
          <FilterLabel>Rango</FilterLabel>
          {/* Toggle relativo vs absoluto */}
          <div style={{
            display: 'flex', gap: 0, marginBottom: 8,
            borderRadius: 4, border: '1px solid var(--border)',
            overflow: 'hidden',
          }}>
            {[
              { id: 'relative', label: 'Reciente' },
              { id: 'absolute', label: 'Fecha y hora' },
            ].map(m => (
              <button
                key={m.id}
                onClick={() => setRangeMode(m.id)}
                style={{
                  flex: 1, padding: '5px 8px', fontSize: 11, fontWeight: 600,
                  border: 'none', cursor: 'pointer',
                  background: rangeMode === m.id
                    ? 'var(--accent)' : 'var(--bg-muted)',
                  color: rangeMode === m.id
                    ? '#fff' : 'var(--text-secondary)',
                }}
              >{m.label}</button>
            ))}
          </div>
          {rangeMode === 'relative' ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {QUICK_RANGES.map(r => (
                <button
                  key={r.id}
                  onClick={() => setWindowMins(r.mins)}
                  style={{
                    padding: '4px 9px', fontSize: 11, fontWeight: 600,
                    borderRadius: 4, border: 'none', cursor: 'pointer',
                    background: windowMins === r.mins
                      ? 'var(--accent)' : 'var(--bg-muted)',
                    color: windowMins === r.mins
                      ? '#fff' : 'var(--text-secondary)',
                  }}
                >{r.label}</button>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <input
                type="date"
                value={absDate}
                max={todayLocalISO()}
                onChange={e => setAbsDate(e.target.value)}
                style={selectStyle}
              />
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>desde</span>
                <select value={absFromH}
                        onChange={e => setAbsFromH(Number(e.target.value))}
                        style={{ ...selectStyle, flex: 1 }}>
                  {Array.from({length: 24}, (_, i) => (
                    <option key={i} value={i}>{String(i).padStart(2,'0')}:00</option>
                  ))}
                </select>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>hasta</span>
                <select value={absToH}
                        onChange={e => setAbsToH(Number(e.target.value))}
                        style={{ ...selectStyle, flex: 1 }}>
                  {Array.from({length: 24}, (_, i) => (
                    <option key={i} value={i}>{String(i).padStart(2,'0')}:59</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: 14, borderBottom: '1px solid var(--border)' }}>
          <FilterLabel>{_fgEN ? 'Cameras' : 'Cámaras'} ({pickedCams.length}/{cameras.length})</FilterLabel>
          <div style={{
            maxHeight: 160, overflow: 'auto',
            border: '1px solid var(--border)', borderRadius: 4,
            background: 'var(--bg-root)',
          }}>
            {cameras.map(c => (
              <label key={c.channel} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 8px', fontSize: 11,
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={pickedCams.includes(c.name)}
                  onChange={e => {
                    setPickedCams(prev => e.target.checked
                      ? [...prev, c.name]
                      : prev.filter(x => x !== c.name))
                  }}
                />
                <span>CH{String(c.channel).padStart(2,'0')} · {c.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div style={{ padding: 14, borderBottom: '1px solid var(--border)' }}>
          <FilterLabel>Objetos</FilterLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {AVAILABLE_CLASSES.map(c => {
              const on = pickedClasses.includes(c.value)
              const sev = severityOf(c.value)
              return (
                <button
                  key={c.value}
                  onClick={() => setPickedClasses(prev => on
                    ? prev.filter(x => x !== c.value)
                    : [...prev, c.value])}
                  style={{
                    padding: '4px 9px', fontSize: 11, fontWeight: 600,
                    borderRadius: 4, cursor: 'pointer',
                    border: on
                      ? `1px solid ${SEV_COLOR[sev]}`
                      : '1px solid var(--border)',
                    background: on ? `${SEV_COLOR[sev]}20` : 'var(--bg-muted)',
                    color: on ? SEV_COLOR[sev] : 'var(--text-secondary)',
                  }}
                >{c.label}</button>
              )
            })}
          </div>
        </div>

        <div style={{ padding: 14, borderBottom: '1px solid var(--border)' }}>
          <FilterLabel>Severidad</FilterLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {SEVERITIES.map(s => {
              const on = pickedSeverities.includes(s.value)
              return (
                <button
                  key={s.value}
                  onClick={() => setPickedSeverities(prev => on
                    ? prev.filter(x => x !== s.value)
                    : [...prev, s.value])}
                  style={{
                    padding: '4px 9px', fontSize: 11, fontWeight: 700,
                    borderRadius: 4, cursor: 'pointer',
                    border: on
                      ? `1px solid ${SEV_COLOR[s.value]}`
                      : '1px solid var(--border)',
                    background: on ? `${SEV_COLOR[s.value]}25` : 'var(--bg-muted)',
                    color: on ? SEV_COLOR[s.value] : 'var(--text-secondary)',
                  }}
                >{s.label}</button>
              )
            })}
          </div>
          <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 4 }}>
            Sin selección = todas
          </div>
        </div>

        {/* Patente — solo aparece si LPR (Patente) está en clases */}
        {pickedClasses.includes('license_plate') && (
          <div style={{ padding: 14, borderBottom: '1px solid var(--border)' }}>
            <FilterLabel>Patente contiene</FilterLabel>
            <input
              type="text"
              value={plateQuery}
              onChange={e => setPlateQuery(e.target.value)}
              placeholder="ej. ABCD12"
              style={{
                ...selectStyle,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontFamily: 'monospace',
              }}
            />
          </div>
        )}

        <div style={{ padding: 14, borderBottom: '1px solid var(--border)' }}>
          <FilterLabel>
            Confianza mínima · {Math.round(minConfidence * 100)}%
          </FilterLabel>
          <input
            type="range"
            min="0.5" max="0.95" step="0.05"
            value={minConfidence}
            onChange={e => setMinConfidence(Number(e.target.value))}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ padding: 14 }}>
          <button
            onClick={runSearch}
            disabled={loading || !siteId}
            style={{
              width: '100%', padding: '10px 16px',
              fontSize: 13, fontWeight: 600,
              borderRadius: 6, border: 'none', cursor: 'pointer',
              background: 'var(--accent)', color: '#fff',
              opacity: loading || !siteId ? 0.5 : 1,
            }}
          >{loading ? 'Buscando…' : 'Buscar'}</button>
        </div>
      </div>

      {/* ─── Modal de detalle ──────────────────────────────────────── */}
      {openItem && (
        <SensorModal
          item={openItem}
          frameUrl={buildSensorUrl(openItem.payload?.sensor_uuid, 'frame')}
          clipUrl={buildClipUrl(openItem.payload?.sensor_uuid)}
          siteId={siteId}
          onClose={() => setOpenItem(null)}
        />
      )}
    </div>
  )
}


// ─── Subcomponentes ──────────────────────────────────────────────────

function FilterLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
      color: 'var(--text-muted)', textTransform: 'uppercase',
      marginBottom: 6,
    }}>{children}</div>
  )
}

const selectStyle = {
  width: '100%', padding: '6px 8px', fontSize: 12,
  borderRadius: 4, border: '1px solid var(--border)',
  background: 'var(--bg-root)', color: 'var(--text-primary)',
}


function SensorThumb({ item, cropUrl, onClick }) {
  const lbl = labelOfEvent(item)
  const sev = severityOf(lbl)
  const ts  = new Date(item.ts)
  return (
    <div
      onClick={onClick}
      style={{
        background: '#000', borderRadius: 6, overflow: 'hidden',
        cursor: 'pointer', position: 'relative',
        border: `2px solid ${SEV_COLOR[sev]}40`,
        transition: 'transform 0.1s, border-color 0.1s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.borderColor = SEV_COLOR[sev] }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.borderColor = SEV_COLOR[sev] + '40' }}
    >
      {cropUrl ? (
        <img
          src={cropUrl}
          alt=""
          loading="lazy"
          style={{
            width: '100%', aspectRatio: '4/3',
            objectFit: 'cover', display: 'block',
          }}
        />
      ) : (
        <div style={{
          width: '100%', aspectRatio: '4/3',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#666', fontSize: 10,
        }}>Sin imagen</div>
      )}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)',
        padding: '14px 6px 4px 6px',
        fontSize: 9, color: '#e5e5e5',
        display: 'flex', flexDirection: 'column', gap: 1,
      }}>
        <span style={{ fontWeight: 600 }}>
          {labelDisplay(lbl)}
          {item.plate && ` · ${item.plate}`}
        </span>
        <span style={{ opacity: 0.7 }}>
          {item.camera_name} · {ts.toLocaleTimeString()}
        </span>
      </div>
      <span style={{
        position: 'absolute', top: 4, right: 4,
        fontSize: 9, fontWeight: 700, padding: '1px 5px',
        borderRadius: 8,
        background: SEV_COLOR[sev], color: '#fff',
      }}>{sev.toUpperCase()[0]}</span>
    </div>
  )
}


function SensorModal({ item, frameUrl, clipUrl, siteId, onClose }) {
  const lbl = labelOfEvent(item)
  const sev = severityOf(lbl)
  const ts  = new Date(item.ts)
  // clipPlaying = true cuando el operador hizo clic en "Ver Clip 6s"
  // → reemplaza el <img> del frame por <video> del clip MP4. Si el
  // backend devuelve 404 (todavía generándose o expirado), el operador
  // ve el mensaje fallback del <video>.
  const [clipPlaying, setClipPlaying] = useState(false)
  const [clipError,   setClipError]   = useState(null)

  // "Ver video NVR" abre la pestaña Reproducción del Monitor pre-poblada
  // con sitio, cámara, fecha, hora y ventana centrada en el sensor.
  // PlaybackMonitor lee los query params al montar y los aplica al state.
  //
  // Mapeo:
  //   ?tab=playback              → MonitorView abre la tab Reproducción
  //   ?site=<int>                → siteId
  //   ?camera_name=<str>         → fila exacta del remote_config (igual al
  //                                que tiene el evento — más portable que
  //                                channel porque sobrevive renumeración)
  //   ?date=YYYY-MM-DD           → fecha local
  //   ?hour=<0-23>               → hora donde inicia la ventana (la
  //                                redondeamos hacia atrás 1 minuto para
  //                                que el evento quede dentro)
  //   ?window=<5|15|30|60>       → tamaño de la ventana
  const openNvrPlayback = () => {
    const eventTs = ts
    const date    = `${eventTs.getFullYear()}-${String(eventTs.getMonth()+1).padStart(2,'0')}-${String(eventTs.getDate()).padStart(2,'0')}`
    const hour    = eventTs.getHours()
    const params = new URLSearchParams({
      tab: 'playback',
      site: String(siteId),
      camera_name: item.camera_name,
      date,
      hour: String(hour),
      window: '15',
    })
    window.open(`/monitor?${params}`, '_blank')
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 40,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-surface)', borderRadius: 10,
          padding: 16, maxWidth: '90vw', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', gap: 10,
          overflow: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>
            {labelDisplay(lbl)}
          </span>
          {item.plate && (
            <span style={{
              fontSize: 12, fontWeight: 700, padding: '2px 8px',
              borderRadius: 4, background: '#3b82f6', color: '#fff',
              fontFamily: 'monospace',
            }}>{item.plate}</span>
          )}
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 6px',
            borderRadius: 4, background: SEV_COLOR[sev], color: '#fff',
          }}>{sev.toUpperCase()}</span>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{
            padding: '4px 10px', fontSize: 12, cursor: 'pointer',
            border: 'none', borderRadius: 4,
            background: 'var(--bg-muted)', color: 'var(--text-secondary)',
          }}>Cerrar ✕</button>
        </div>

        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {item.camera_name} · {item.zone} · {ts.toLocaleString()}
          {' · '}conf {(item.confidence * 100).toFixed(0)}%
        </div>

        {!clipPlaying && frameUrl && (
          <img
            src={frameUrl}
            alt=""
            style={{
              maxWidth: 'min(900px, 90vw)',
              maxHeight: '60vh',
              objectFit: 'contain',
              borderRadius: 6,
              background: '#000',
            }}
          />
        )}

        {clipPlaying && clipUrl && (
          <div style={{ position: 'relative' }}>
            <video
              src={clipUrl}
              controls
              autoPlay
              preload="metadata"
              onError={() => setClipError('No se pudo cargar el clip — probablemente todavía está generándose (espera 5-10s) o ya fue purgado.')}
              style={{
                maxWidth: 'min(900px, 90vw)',
                maxHeight: '60vh',
                background: '#000',
                borderRadius: 6,
                width: '100%',
              }}
            />
            {clipError && (
              <div style={{
                position: 'absolute', top: 12, left: 12,
                background: 'rgba(0,0,0,0.85)', color: '#fff',
                padding: '8px 12px', borderRadius: 4, fontSize: 11,
                maxWidth: '60%',
              }}>{clipError}</div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
          {clipUrl && !clipPlaying && (
            <button
              onClick={() => { setClipError(null); setClipPlaying(true) }}
              style={{
                padding: '8px 14px', fontSize: 12, fontWeight: 600,
                borderRadius: 6, border: 'none', cursor: 'pointer',
                background: '#22c55e', color: '#fff',
              }}
              title="Clip pre-recortado del momento exacto del evento (3s antes + 3s después). Reproducción instantánea."
            >▶ Ver Clip 6s</button>
          )}
          {clipPlaying && (
            <button
              onClick={() => { setClipPlaying(false); setClipError(null) }}
              style={{
                padding: '8px 14px', fontSize: 12, fontWeight: 600,
                borderRadius: 6, border: 'none', cursor: 'pointer',
                background: 'var(--bg-muted)', color: 'var(--text-secondary)',
              }}
            >← Volver al frame</button>
          )}
          <button
            onClick={openNvrPlayback}
            style={{
              padding: '8px 14px', fontSize: 12, fontWeight: 600,
              borderRadius: 6, border: 'none', cursor: 'pointer',
              background: 'var(--accent)', color: '#fff',
            }}
          >Ver video del NVR (±30s)</button>
          <span style={{
            fontSize: 10, color: 'var(--text-muted)',
            alignSelf: 'center',
          }}>
            Clip 6s: instantáneo desde edge. NVR: depende de que aún tenga la grabación.
          </span>
        </div>
      </div>
    </div>
  )
}


// ─── Helpers ─────────────────────────────────────────────────────────

// Mapea el event_type del backend al label del catálogo de clases.
function labelOfEvent(it) {
  const t = it.event_type
  if (t === 'lpr') return 'license_plate'
  if (t === 'person_detected' || t === 'person_summary') return 'person'
  if (t === 'vehicle_detected' || t === 'vehicle_summary') {
    return it.payload?.label || it.payload?.vehicle_type || 'car'
  }
  // Normalizar nuevos event_types al value del catálogo AVAILABLE_CLASSES
  // (sino el filtro client-side los descarta porque busca 'loitering' pero
  // el event_type real es 'loitering_detected').
  if (t === 'loitering_detected') return 'loitering'
  if (t === 'weapon_detected')    return 'weapon'
  if (t === 'face_match')         return 'face'
  return t
}

function labelDisplay(lbl) {
  const map = {
    person: 'Persona',
    car: 'Auto', truck: 'Camión', bus: 'Bus',
    motorcycle: 'Moto', bicycle: 'Bicicleta',
    license_plate: 'Patente',
    loitering: 'Merodeo', line_crossing: 'Cruce línea',
  }
  return map[lbl] || lbl
}